import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Heart, Leaf, BookOpen, ArrowRight } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import SocialShare from '../components/SocialShare';

const TCM = () => (
  <div className="space-y-8">
    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8">
      <h2 className="text-3xl font-bold text-gray-900 mb-6">Traditional Chinese Medicine</h2>
      <div className="prose max-w-none">
        <p className="text-gray-700 leading-relaxed mb-4">
          Dr. Lim's expertise in Traditional Chinese Medicine forms a cornerstone of his wellness philosophy. His deep study of classical texts, particularly Shang Han Lun (Treatise on Cold Damage Disorders), combined with modern applications, offers a unique bridge between ancient wisdom and contemporary health needs.
        </p>
        <p className="text-gray-700 leading-relaxed">
          Through systematic teachings, he makes these profound healing traditions accessible to modern practitioners and wellness seekers worldwide.
        </p>
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Key Areas of Study</h3>
        <ul className="space-y-3">
          <li className="flex items-start space-x-3">
            <div className="w-2 h-2 rounded-full bg-amber-600 mt-2"></div>
            <div>
              <h4 className="font-semibold text-gray-900">Shang Han Lun</h4>
              <p className="text-sm text-gray-600">Classical formulations and diagnostic methods</p>
            </div>
          </li>
          <li className="flex items-start space-x-3">
            <div className="w-2 h-2 rounded-full bg-amber-600 mt-2"></div>
            <div>
              <h4 className="font-semibold text-gray-900">Herbal Medicine</h4>
              <p className="text-sm text-gray-600">Individual herbs and synergistic combinations</p>
            </div>
          </li>
          <li className="flex items-start space-x-3">
            <div className="w-2 h-2 rounded-full bg-amber-600 mt-2"></div>
            <div>
              <h4 className="font-semibold text-gray-900">Diagnostic Methods</h4>
              <p className="text-sm text-gray-600">Pulse reading, tongue diagnosis, constitutional assessment</p>
            </div>
          </li>
        </ul>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Modern Applications</h3>
        <ul className="space-y-3">
          <li className="flex items-start space-x-3">
            <div className="w-2 h-2 rounded-full bg-emerald-600 mt-2"></div>
            <div>
              <h4 className="font-semibold text-gray-900">Preventive Medicine</h4>
              <p className="text-sm text-gray-600">Focus on maintaining health and preventing disease</p>
            </div>
          </li>
          <li className="flex items-start space-x-3">
            <div className="w-2 h-2 rounded-full bg-emerald-600 mt-2"></div>
            <div>
              <h4 className="font-semibold text-gray-900">Integration with Modern Health</h4>
              <p className="text-sm text-gray-600">Complementary approach to contemporary medicine</p>
            </div>
          </li>
          <li className="flex items-start space-x-3">
            <div className="w-2 h-2 rounded-full bg-emerald-600 mt-2"></div>
            <div>
              <h4 className="font-semibold text-gray-900">Lifestyle Medicine</h4>
              <p className="text-sm text-gray-600">Holistic approach to health and wellness</p>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
);

const Ganotherapy = () => (
  <div className="space-y-8">
    <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-8">
      <h2 className="text-3xl font-bold text-gray-900 mb-6">Ganotherapy: The Science of Ganoderma</h2>
      <div className="prose max-w-none">
        <p className="text-gray-700 leading-relaxed mb-4">
          Ganotherapy represents Dr. Lim's pioneering work in making Ganoderma Lucidum (Reishi/Lingzhi) a globally recognized wellness solution. With 20+ years of dedicated research, he has bridged 2000+ years of Traditional Chinese Medicine wisdom with modern scientific validation.
        </p>
        <p className="text-gray-700 leading-relaxed">
          Ganoderma, known as the "King of Herbs," has been revered in Eastern medicine for its remarkable adaptogenic and health-supporting properties.
        </p>
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="bg-white rounded-xl p-6 shadow-md border border-red-200">
        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
          <Leaf className="text-red-600" size={24} />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-3">What is Ganoderma?</h3>
        <p className="text-sm text-gray-600">
          Also known as Reishi or Lingzhi, Ganoderma Lucidum is a medicinal mushroom used in Traditional Chinese Medicine for over 2000 years for its immune-supporting and adaptogenic properties.
        </p>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-md border border-red-200">
        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mb-4">
          <BookOpen className="text-amber-600" size={24} />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-3">20+ Years Research</h3>
        <p className="text-sm text-gray-600">
          Dr. Lim's extensive research has focused on cultivation methods, extraction techniques, and practical applications of Ganoderma in modern wellness protocols.
        </p>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-md border border-red-200">
        <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
          <Heart className="text-emerald-600" size={24} />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-3">DXN Integration</h3>
        <p className="text-sm text-gray-600">
          DXN's complete vertical integration ensures quality from organic cultivation through to consumer products, maintaining the integrity of Ganoderma's beneficial compounds.
        </p>
      </div>
    </div>

    <div className="bg-white rounded-xl p-8 shadow-md border border-gray-100">
      <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Key Research Areas</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="font-bold text-gray-900 mb-3 text-lg">Cultivation & Quality</h4>
          <ul className="space-y-2">
            <li className="flex items-start space-x-2">
              <span className="text-red-600 mt-1">•</span>
              <span className="text-gray-700 text-sm">Organic farming methods</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-red-600 mt-1">•</span>
              <span className="text-gray-700 text-sm">Optimal growing conditions</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-red-600 mt-1">•</span>
              <span className="text-gray-700 text-sm">Harvesting timing for maximum potency</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-red-600 mt-1">•</span>
              <span className="text-gray-700 text-sm">GMP-certified processing</span>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="font-bold text-gray-900 mb-3 text-lg">Health Applications</h4>
          <ul className="space-y-2">
            <li className="flex items-start space-x-2">
              <span className="text-emerald-600 mt-1">•</span>
              <span className="text-gray-700 text-sm">Immune system support</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-emerald-600 mt-1">•</span>
              <span className="text-gray-700 text-sm">Adaptogenic properties</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-emerald-600 mt-1">•</span>
              <span className="text-gray-700 text-sm">Overall wellness maintenance</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-emerald-600 mt-1">•</span>
              <span className="text-gray-700 text-sm">Traditional Chinese Medicine integration</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
);

const Wellness = () => {
  const { section } = useParams();

  const sections = [
    { id: 'tcm', name: 'Traditional Chinese Medicine', component: TCM },
    { id: 'ganotherapy', name: 'Ganotherapy', component: Ganotherapy },
    { id: 'herbal', name: 'Herbal Medicine', component: () => <div className="text-center py-20"><h2 className="text-2xl font-bold text-gray-900">Herbal Medicine - Coming Soon</h2></div> }
  ];

  const currentSection = section || 'tcm';
  const CurrentComponent = sections.find(s => s.id === currentSection)?.component || TCM;

  return (
    <div className="min-h-screen pt-20">
      <SEOHead 
        title="TCM & Wellness"
        description="Explore Traditional Chinese Medicine, Ganotherapy, and herbal medicine with Dr. Lim Siow Jin. 20+ years of Ganoderma research meeting 2000+ years of TCM wisdom."
        keywords="Traditional Chinese Medicine, Ganotherapy, Ganoderma Lucidum, Shang Han Lun, herbal medicine, TCM wellness, Dr. Lim Siow Jin wellness"
        
      />
      
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-700 via-emerald-600 to-teal-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">TCM & Wellness</h1>
              <p className="text-xl text-emerald-100">Ancient wisdom meets modern science for holistic health</p>
            </div>
            <SocialShare 
              title="TCM & Wellness - Dr. Lim Siow Jin"
              description="Explore Traditional Chinese Medicine, Ganotherapy, and herbal medicine wisdom."
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-1 overflow-x-auto">
            {sections.map((sec) => (
              <Link
                key={sec.id}
                to={`/wellness/${sec.id}`}
                className={`px-6 py-4 font-medium text-sm whitespace-nowrap border-b-2 transition-colors ${
                  currentSection === sec.id
                    ? 'border-emerald-600 text-emerald-700'
                    : 'border-transparent text-gray-600 hover:text-emerald-700 hover:border-gray-300'
                }`}
              >
                {sec.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <CurrentComponent />
      </div>
    </div>
  );
};

export default Wellness;